import React from 'react';
import { Typography, Box, Divider } from '@mui/material';
import styled from '@emotion/styled';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import TransformIcon from '@mui/icons-material/Transform';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewListIcon from '@mui/icons-material/ViewList';
import OnboardingVideoPlayer from 'components/v2/onboarding/OnboardingVideoPlayer';

// ==========================================
// Tutoriel du dashboard V2. Le tutoriel V3 (nouvelle arborescence de pages,
// contenu adapte au role) vit dans components/v3/onboarding/OnboardingContent.js.
// ==========================================

const SlideContainer = styled(Box)({
    padding: '2em 1em',
    maxHeight: '60vh',
    overflowY: 'auto',
    overflowX: 'hidden',
});

const SlideTitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 700,
    fontSize: 'clamp(20px, 2vw, 28px)',
    color: theme?.palette?.primary?.main || '#0072E4',
    marginBottom: '0.5em',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5em',
}));

const SlideSubtitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 600,
    fontSize: 'clamp(16px, 1.5vw, 20px)',
    color: theme?.palette?.text?.primary || '#333',
    marginTop: '1.5em',
    marginBottom: '0.8em',
}));

const SlideText = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 400,
    fontSize: 'clamp(14px, 1.2vw, 16px)',
    color: theme?.palette?.text?.secondary || '#666',
    lineHeight: 1.6,
    marginBottom: '1em',
}));

const BulletList = styled('ul')({
    marginLeft: '1.5em',
    marginBottom: '1em',
});

const BulletItem = styled('li')(({ theme }) => ({
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: 'clamp(14px, 1.2vw, 16px)',
    color: theme?.palette?.text?.secondary || '#666',
    lineHeight: 1.8,
    marginBottom: '0.5em',
}));

const HighlightBox = styled(Box)(({ theme }) => ({
    backgroundColor: '#F0F8FF',
    padding: '1em',
    borderRadius: '10px',
    borderLeft: `4px solid ${theme?.palette?.primary?.main || '#0072E4'}`,
    marginTop: '1em',
    marginBottom: '1em',
}));

const StyledDivider = styled(Divider)({
    margin: '2em 0',
});

const IconCircle = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: theme?.palette?.primary?.main || '#0072E4',
    color: 'white',
}));

/**
 * @returns {Array} - Slides du tutoriel du dashboard V2
 */
export const getOnboardingSlides = () => {
    return [
        // Slide 1 : Bienvenue
        {
            title: 'Bienvenue sur MOBIUSpack 👋',
            content: (
                <SlideContainer>
                    <SlideTitle>Bienvenue sur MOBIUSpack 👋</SlideTitle>
                    <SlideText>
                        Ce guide vous présente les <strong>4 pages du dashboard</strong> pour piloter vos emballages réemployables en toute autonomie.
                    </SlideText>
                    <SlideText>
                        Prenez quelques minutes pour découvrir comment tirer le meilleur parti de votre plateforme.
                    </SlideText>
                    <SlideText>
                        Vous pourrez naviguer à votre rythme entre les différentes sections et revenir à ce tutoriel à tout moment depuis le menu.
                    </SlideText>
                </SlideContainer>
            ),
        },

        // Slide 2 : Vidéo
        {
            title: 'Découvrez MOBIUSpack en vidéo',
            content: (
                <SlideContainer>
                    <SlideTitle>Découvrez MOBIUSpack en vidéo</SlideTitle>
                    <SlideText>
                        Pour une vue d'ensemble de la plateforme, regardez cette présentation vidéo qui vous guide à travers les principales fonctionnalités.
                    </SlideText>
                    <Box sx={{ marginTop: '2em', marginBottom: '2em' }}>
                        <OnboardingVideoPlayer autoplay={false} muted={true} />
                    </Box>
                </SlideContainer>
            ),
        },

        // Slide 3 : Page Décisionnel
        {
            title: '1 · Page Décisionnel',
            content: (
                <SlideContainer>
                    <SlideTitle>
                        <IconCircle><AutoGraphIcon /></IconCircle>
                        1 · Page Décisionnel
                    </SlideTitle>
                    <SlideText>
                        C'est la première page que vous voyez après connexion. Elle vous donne une <strong>vue d'ensemble de votre flotte</strong>.
                    </SlideText>

                    <SlideSubtitle>6 indicateurs clés</SlideSubtitle>
                    <BulletList>
                        <BulletItem>
                            <strong>Emballages en circulation</strong> — le nombre d'emballages actifs dans votre réseau.
                        </BulletItem>
                        <BulletItem>
                            <strong>Estimés perdus</strong> — les emballages sans scan depuis le seuil que vous avez défini.
                        </BulletItem>
                        <BulletItem>
                            <strong>Cycles réalisés</strong> — le nombre total de rotations effectuées.
                        </BulletItem>
                        <BulletItem>
                            <strong>Durée moyenne d'utilisation</strong> — le temps moyen entre expédition et retour d'un emballage.
                        </BulletItem>
                    </BulletList>

                    <SlideSubtitle>2 graphiques</SlideSubtitle>
                    <BulletList>
                        <BulletItem>
                            <strong>Tendance du stock</strong> — à gauche de l'axe : votre historique. À droite : une projection basée sur votre activité d'expédition et de retour.
                        </BulletItem>
                        <BulletItem>
                            <strong>Utilisation mensuelle de la flotte</strong> — la courbe verte représente votre objectif d'utilisation, la bleue vos rotations réalisées. La zone rouge indique si vous n'avez pas atteint votre objectif sur la période.
                        </BulletItem>
                    </BulletList>

                    <HighlightBox>
                        <SlideText sx={{ marginBottom: 0 }}>
                            🎛️ <strong>Filtres disponibles sur toutes les vues :</strong> lieu, SKU, référence de modèle, plage de dates.
                        </SlideText>
                    </HighlightBox>
                </SlideContainer>
            ),
        },

        // Slide 4 : Page Opérationnel
        {
            title: '2 · Page Opérationnel',
            content: (
                <SlideContainer>
                    <SlideTitle>
                        <IconCircle><TransformIcon /></IconCircle>
                        2 · Page Opérationnel
                    </SlideTitle>
                    <SlideText>
                        Analysez en détail la <strong>répartition et l'état</strong> de vos emballages.
                    </SlideText>

                    <SlideSubtitle>Répartition du stock par statut</SlideSubtitle>
                    <SlideText>
                        Vos emballages sont répartis en trois statuts : <strong>en stock</strong>, <strong>en transit chez un site</strong>, ou <strong>endommagés</strong>. En cliquant sur un histogramme, un panneau latéral s'ouvre avec la répartition par référence d'emballage et par lieu.
                    </SlideText>
                    <SlideText sx={{ fontStyle: 'italic', fontSize: 'clamp(13px, 1.1vw, 15px)' }}>
                        Exemple : 6% du stock, soit 25 unités, se trouvent dans le magasin de Hong Kong.
                    </SlideText>

                    <SlideSubtitle>Usure de la flotte</SlideSubtitle>
                    <SlideText>
                        Cet indicateur compare les rotations réalisées au potentiel estimé de votre flotte. Il vous alerte lorsque vos emballages approchent de leur limite de cycles garantis.
                    </SlideText>
                    <SlideText sx={{ fontStyle: 'italic', fontSize: 'clamp(13px, 1.1vw, 15px)' }}>
                        Exemple : pour une flotte de 480 emballages conçue pour 600 cycles, si vous avez déjà réalisé 15 000 cycles, il est temps d'anticiper le renouvellement.
                    </SlideText>
                </SlideContainer>
            ),
        },

        // Slide 5 : Page Historique
        {
            title: '3 · Page Historique',
            content: (
                <SlideContainer>
                    <SlideTitle>
                        <IconCircle><RestoreIcon /></IconCircle>
                        3 · Page Historique
                    </SlideTitle>
                    <SlideText>
                        Accédez à l'ensemble des <strong>données brutes</strong> de votre flotte sur la période de votre choix.
                    </SlideText>

                    <BulletList>
                        <BulletItem>Sélectionnez la plage de dates souhaitée.</BulletItem>
                        <BulletItem>Consultez l'ensemble des scans dans le tableau.</BulletItem>
                        <BulletItem>
                            Exportez les données en Excel via le bouton <strong>"Export de données"</strong>.
                        </BulletItem>
                    </BulletList>

                    <HighlightBox>
                        <SlideText sx={{ marginBottom: 0 }}>
                            📅 L'historique couvre <strong>12 mois glissants</strong> avec archivage sur 10 ans.
                        </SlideText>
                    </HighlightBox>
                </SlideContainer>
            ),
        },

        // Slide 6 : Page Configuration
        {
            title: '4 · Page Configuration',
            content: (
                <SlideContainer>
                    <SlideTitle>
                        <IconCircle><SettingsIcon /></IconCircle>
                        4 · Page Configuration
                    </SlideTitle>
                    <SlideText>
                        Paramétrez votre compte et gérez vos sites, agences et flux logistiques.
                    </SlideText>

                    <SlideSubtitle>
                        Seuils & paramètres <span style={{ fontSize: '0.85em', fontStyle: 'italic', fontWeight: 400 }}>(droits administrateur requis)</span>
                    </SlideSubtitle>
                    <BulletList>
                        <BulletItem>
                            <strong>Durée avant déclaration de perte</strong> — après combien de jours sans scan un emballage est considéré perdu.
                        </BulletItem>
                        <BulletItem>
                            <strong>Garantie d'utilisation</strong> — le nombre de cycles couverts par la garantie fabricant.
                        </BulletItem>
                        <BulletItem>
                            <strong>Stock minimal</strong> — le seuil en dessous duquel vous souhaitez être alerté pour éviter la rupture.
                        </BulletItem>
                    </BulletList>

                    <SlideSubtitle>Gestion des sites & flux</SlideSubtitle>
                    <SlideText>
                        Retrouvez tous vos sites, agences et magasins déclarés, ainsi que les actions autorisées par localisation : <strong>expédition, réception, retour</strong>. Cette configuration garantit que chaque utilisateur n'agit que sur son périmètre.
                    </SlideText>
                </SlideContainer>
            ),
        },

        // Slide 7 : Vues partagées
        {
            title: 'Vues partagées',
            content: (
                <SlideContainer>
                    <SlideTitle>
                        <IconCircle><ViewListIcon /></IconCircle>
                        Vues partagées
                    </SlideTitle>
                    <SlideText>
                        Les <strong>vues partagées</strong> sont des rapports pré-configurés accessibles à tous les utilisateurs de votre organisation. Elles facilitent la collaboration et harmonisent le pilotage de votre flotte.
                    </SlideText>

                    <SlideSubtitle>Rôle des vues partagées</SlideSubtitle>
                    <BulletList>
                        <BulletItem>
                            <strong>Standardisation des rapports</strong> — tous les collaborateurs accèdent aux mêmes indicateurs clés avec la même structure.
                        </BulletItem>
                        <BulletItem>
                            <strong>Dates dynamiques</strong> — les filtres temporels s'ajustent automatiquement (mois en cours, semaine actuelle, etc.).
                        </BulletItem>
                        <BulletItem>
                            <strong>Accès continu</strong> — visualisez instantanément les données sans avoir à recréer vos filtres.
                        </BulletItem>
                    </BulletList>

                    <SlideSubtitle>Pertinence pour l'équipe</SlideSubtitle>
                    <SlideText>
                        Les vues partagées permettent de <strong>gagner du temps</strong> en évitant à chacun de reconfigurer les mêmes paramètres. Elles assurent une <strong>cohérence</strong> dans le suivi des performances à l'échelle de l'organisation.
                    </SlideText>

                    <HighlightBox>
                        <SlideText sx={{ marginBottom: 0 }}>
                            🔒 <strong>Droits d'édition</strong> : Seuls les administrateurs et les gestionnaires de compte peuvent créer ou modifier des vues partagées. Tous les utilisateurs peuvent les consulter.
                        </SlideText>
                    </HighlightBox>
                </SlideContainer>
            ),
        },

        // Slide 8 : Conclusion
        {
            title: 'Vous êtes prêt !',
            content: (
                <SlideContainer>
                    <SlideTitle>Vous êtes prêt ! 🚀</SlideTitle>
                    <SlideText>
                        Explorez chaque section à votre rythme. Vous avez maintenant toutes les clés pour piloter efficacement votre flotte d'emballages réemployables.
                    </SlideText>

                    <StyledDivider />

                    <SlideText>
                        <strong>Une question ?</strong> Notre équipe est à votre disposition pour vous accompagner.
                    </SlideText>
                    <SlideText>
                        Contactez-nous à <strong style={{ color: '#0072E4' }}>tech@mobiuspack.com</strong>
                    </SlideText>

                    <HighlightBox>
                        <SlideText sx={{ marginBottom: 0 }}>
                            💡 Vous pouvez relancer ce tutoriel à tout moment depuis le menu <strong>Tutoriel V2</strong>.
                        </SlideText>
                    </HighlightBox>
                </SlideContainer>
            ),
        },
    ];
};
